const RELOAD_FLAG_KEY = 'prescia-stale-client-bundle-reload'

/**
 * How long a healthy page must stay up before another skew can reload the tab.
 * Shorter than a deploy, long enough that a still-broken page does not loop.
 */
const CLEAR_RELOAD_FLAG_AFTER_MS = 10_000

function errorText(error: unknown): string {
  if (!(error instanceof Error)) return ''
  return `${error.name} ${error.message}`
}

/**
 * A client bundle and the RSC payload that referenced it came from different
 * builds, or a chunk failed to load. `reset()` re-renders the same tree.
 */
export function isStaleClientBundleError(error: unknown): boolean {
  const text = errorText(error)
  if (text.length === 0) return false
  return (
    text.includes('ChunkLoadError') ||
    text.includes('module factory is not available') ||
    text.includes('Failed to load chunk') ||
    /Loading chunk [\w.-]+ failed/i.test(text)
  )
}

/**
 * Reload the document once so the next response is one deployment's HTML and
 * chunks. Returns true when a reload was started. A second call in the same
 * tab leaves the caller to show the fallback.
 */
export function reloadOnceForStaleClientBundle(error: unknown): boolean {
  if (typeof window === 'undefined') return false
  if (!isStaleClientBundleError(error)) return false

  try {
    if (sessionStorage.getItem(RELOAD_FLAG_KEY) === '1') return false
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1')
  } catch {
    return false
  }

  window.location.reload()
  return true
}

/**
 * Full reload from a retry control. Unlike `reset()`, this fetches a document
 * that matches the current deployment.
 */
export function reloadStaleClientBundle(): void {
  if (typeof window === 'undefined') return
  window.location.reload()
}

/**
 * Allow a later deploy to reload this tab once. Called from a page that has
 * stayed mounted, so a broken error screen is not cleared before it can see
 * the flag.
 */
export function clearStaleClientBundleReloadFlag(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY)
  } catch {
    // sessionStorage can throw when storage is blocked.
  }
}

export function scheduleClearStaleClientBundleReloadFlag(): () => void {
  if (typeof window === 'undefined') return () => undefined
  const timeoutId = window.setTimeout(
    clearStaleClientBundleReloadFlag,
    CLEAR_RELOAD_FLAG_AFTER_MS,
  )
  return () => window.clearTimeout(timeoutId)
}
